import React from 'react';
import Icon from '../../../components/AppIcon';
import { Link } from 'react-router-dom';

const AutomotiveHardwareFarmUseCase = () => {
  const keyScenarios = [
    "Remote access to physical HIL rigs and ECU test benches",
    "Unified workspace for physical and virtual device testing",
    "Automated CI/CD integration with hardware-in-the-loop validation"
  ];

  return (
    <section className="py-24 px-4 md:px-6 lg:px-8" style={{ backgroundColor: '#242423' }}>
      <div className="max-w-7xl mx-auto">
        <div 
          className="rounded-xl p-8 md:p-12"
          style={{ backgroundColor: '#2E2E2C', border: '1px solid #3A3A38' }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            
            {/* Left Column - Text Content */}
            <div className="flex flex-col justify-center">
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-semibold uppercase tracking-wider mb-6 w-fit"
                style={{ backgroundColor: 'rgba(245, 203, 92, 0.15)', border: '1px solid rgba(245, 203, 92, 0.4)', color: '#F5CB5C' }}
              >
                Automotive
              </div>

              <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#E8EDDF' }}>
                Remote Hardware Farm
              </h2>

              <p className="text-base mb-4" style={{ color: '#9CA3AF' }}>
                For SDV developers and testing teams
              </p>

              <p className="text-base leading-relaxed mb-8" style={{ color: '#9CA3AF' }}>
                Browser-based development, testing, and deployment unified across physical HIL hardware and virtual device farms. No local setup. No hardware queues. Instant access to distributed testing infrastructure.
              </p>

              <div className="mb-8">
                <h3 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: '#9CA3AF' }}>
                  Key Scenarios
                </h3>
                <ul className="space-y-4">
                  {keyScenarios.map((scenario, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div 
                        className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                        style={{ backgroundColor: '#4FB3B8' }}
                      ></div>
                      <span className="text-base" style={{ color: '#E8EDDF' }}>{scenario}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Link 
                to="/use-cases/automotive-remote-hardware-farm"
                className="inline-flex items-center gap-2 text-base font-medium transition-colors duration-250 w-fit"
                style={{ color: '#4FB3B8' }}
              >
                View Use Case
                <Icon name="ArrowRight" size={16} style={{ color: '#4FB3B8' }} />
              </Link>
            </div>

            {/* Right Column - Diagram */}
            <div className="flex items-center justify-center">
              <div 
                className="w-full rounded-lg p-6"
                style={{ backgroundColor: '#242423', border: '1px solid #3A3A38' }}
              >
                <svg width="100%" height="420" viewBox="0 0 500 420" className="max-w-full">
                  
                  {/* Physical Fleet Section */}
                  <text x="100" y="20" textAnchor="middle" fill="#4FB3B8" fontSize="10" fontWeight="600" letterSpacing="1">PHYSICAL FLEET</text>
                  
                  <rect x="40" y="35" width="120" height="35" rx="4" fill="#242423" stroke="#4FB3B8" strokeWidth="1.5" />
                  <text x="100" y="57" textAnchor="middle" fill="#E8EDDF" fontSize="10" fontWeight="500">ECU 01</text>
                  
                  <rect x="40" y="80" width="120" height="35" rx="4" fill="#242423" stroke="#4FB3B8" strokeWidth="1.5" />
                  <text x="100" y="102" textAnchor="middle" fill="#E8EDDF" fontSize="10" fontWeight="500">ECU 02</text>
                  
                  <rect x="40" y="125" width="120" height="35" rx="4" fill="#242423" stroke="#4FB3B8" strokeWidth="1.5" />
                  <text x="100" y="147" textAnchor="middle" fill="#E8EDDF" fontSize="10" fontWeight="500">ECU 02</text>
                  
                  {/* Virtual Farm Section */}
                  <text x="400" y="20" textAnchor="middle" fill="#9CA3AF" fontSize="10" fontWeight="600" letterSpacing="1">VIRTUAL FARM</text>
                  
                  <rect x="340" y="35" width="120" height="35" rx="4" fill="#242423" stroke="#3A3A38" strokeWidth="1.5" />
                  <text x="400" y="57" textAnchor="middle" fill="#E8EDDF" fontSize="10" fontWeight="500">SIMULATOR A</text>
                  
                  <rect x="340" y="80" width="120" height="35" rx="4" fill="#242423" stroke="#3A3A38" strokeWidth="1.5" />
                  <text x="400" y="102" textAnchor="middle" fill="#E8EDDF" fontSize="10" fontWeight="500">SIMULATOR A</text>
                  
                  <rect x="340" y="125" width="120" height="35" rx="4" fill="#242423" stroke="#3A3A38" strokeWidth="1.5" />
                  <text x="400" y="147" textAnchor="middle" fill="#E8EDDF" fontSize="10" fontWeight="500">VEHICLE B</text>
                  
                  {/* Connection lines from Physical Fleet */}
                  <path d="M 160 52 L 190 52 L 190 190 L 250 190" stroke="#4FB3B8" strokeWidth="1.5" fill="none" />
                  <path d="M 160 97 L 200 97 L 200 190 L 250 190" stroke="#4FB3B8" strokeWidth="1.5" fill="none" />
                  <path d="M 160 142 L 210 142 L 210 190 L 250 190" stroke="#4FB3B8" strokeWidth="1.5" fill="none" />
                  
                  {/* Connection lines from Virtual Farm */}
                  <path d="M 340 52 L 310 52 L 310 190 L 250 190" stroke="#3A3A38" strokeWidth="1.5" fill="none" />
                  <path d="M 340 97 L 300 97 L 300 190 L 250 190" stroke="#3A3A38" strokeWidth="1.5" fill="none" />
                  <path d="M 340 142 L 290 142 L 290 190 L 250 190" stroke="#3A3A38" strokeWidth="1.5" fill="none" />
                  
                  {/* OTA Telemetry Label - Left */}
                  <text x="70" y="220" textAnchor="middle" fill="#4FB3B8" fontSize="9" fontWeight="600">OTA TELEMETRY</text>
                  <line x1="120" y1="217" x2="150" y2="217" stroke="#4FB3B8" strokeWidth="1.5" />
                  
                  {/* Sleeve Orchestration Box */}
                  <rect x="150" y="185" width="200" height="60" rx="6" fill="#242423" stroke="#F5CB5C" strokeWidth="2" />
                  <text x="250" y="210" textAnchor="middle" fill="#E8EDDF" fontSize="11" fontWeight="700">SLEEVE ORCHESTRATION</text>
                  <text x="250" y="228" textAnchor="middle" fill="#9CA3AF" fontSize="8">Test Coordination • OTA Management • Fleet Control</text>
                  
                  {/* OTA Failure Label - Right */}
                  <line x1="350" y1="217" x2="395" y2="217" stroke="#3A3A38" strokeWidth="1.5" />
                  <circle cx="405" cy="217" r="8" fill="#FF6B6B" />
                  <text x="405" y="220" textAnchor="middle" fill="#242423" fontSize="8" fontWeight="bold">!</text>
                  <text x="440" y="220" textAnchor="start" fill="#FF6B6B" fontSize="9" fontWeight="600">OTA FAILURE</text>
                  
                  {/* Line to OTA Approval Diamond */}
                  <line x1="250" y1="245" x2="250" y2="275" stroke="#F5CB5C" strokeWidth="1.5" />
                  
                  {/* OTA Approval Diamond */}
                  <polygon points="250,275 280,300 250,325 220,300" fill="#242423" stroke="#F5CB5C" strokeWidth="1.5" />
                  <text x="250" y="296" textAnchor="middle" fill="#E8EDDF" fontSize="7" fontWeight="600">OTA</text>
                  <text x="250" y="308" textAnchor="middle" fill="#E8EDDF" fontSize="7" fontWeight="600">APPROVAL</text>
                  
                  {/* Lines from Diamond to Human Teams */}
                  <line x1="250" y1="325" x2="250" y2="345" stroke="#F5CB5C" strokeWidth="1.5" />
                  <line x1="150" y1="345" x2="350" y2="345" stroke="#F5CB5C" strokeWidth="1.5" />
                  
                  <line x1="170" y1="345" x2="170" y2="360" stroke="#4FB3B8" strokeWidth="1.5" />
                  <line x1="250" y1="345" x2="250" y2="360" stroke="#4FB3B8" strokeWidth="1.5" />
                  <line x1="330" y1="345" x2="330" y2="360" stroke="#F5CB5C" strokeWidth="1.5" />
                  
                  {/* Human Teams Circles */}
                  <circle cx="170" cy="380" r="22" fill="#242423" stroke="#4FB3B8" strokeWidth="2" />
                  <text x="170" y="384" textAnchor="middle" fill="#E8EDDF" fontSize="9" fontWeight="600">ENGINEERS</text>
                  
                  <circle cx="250" cy="380" r="22" fill="#242423" stroke="#4FB3B8" strokeWidth="2" />
                  <text x="250" y="384" textAnchor="middle" fill="#E8EDDF" fontSize="9" fontWeight="600">QA</text>
                  
                  <circle cx="330" cy="380" r="22" fill="#242423" stroke="#F5CB5C" strokeWidth="2" />
                  <text x="330" y="384" textAnchor="middle" fill="#E8EDDF" fontSize="9" fontWeight="600">OPS</text>
                  
                  {/* Human Teams Label */}
                  <text x="250" y="415" textAnchor="middle" fill="#F5CB5C" fontSize="10" fontWeight="600" letterSpacing="1">HUMAN TEAMS</text>
                  
                </svg>
              </div>
            </div>
            
          </div>
        </div>
      </div>
    </section>
  );
};

export default AutomotiveHardwareFarmUseCase;